/**
 * Utility functions for the TanStack Table v2 implementation of the products table.
 *
 * Provides test ID setter functions following the BCC test ID pattern:
 * - Single source of truth via generateTestId from @client-shared
 * - One setter function per testable element
 * - Row index used for uniqueness in repeating cell elements
 */

import { generateTestId } from "@client-shared";
import type { ProductImage, FormProduct, FormVariant, ShipToInfo, VendorCountryCode } from "@shared/types";
import type { ApprovalStatusResponseRecord } from "@shared/services/salesforce/types";
import { parsePrice } from "@bcc/lib/utils";
import { parseChannelCountryCode, resolveShipToWebStatus } from "@bcc/lib/products/ship-to-countries";
import { resolveProductImage, PLACEHOLDER_IMAGE } from "@bcc/components/shopify-import/utils/get-product-image-url";
import { resolveWebStatus } from "@bcc/lib/products/resolve-web-status";
import { CrossBorderCountriesSchema, type CrossBorderCountries } from "@shared/schemas/enums/crossBorder.enum";
import type { FilterFn } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { VariantWithProductData, type ShipToCountryData } from "./types";
import type { Product } from "../products-table-legacy";

/**
 * Container test ID for the main ProductsTable component
 */
export const setProductsTableTestId = (identifier: string): string => generateTestId("ProductsTable", identifier);

/**
 * Cell test ID setters
 *
 * All cell test IDs use row index for uniqueness.
 * Follows the pattern established in SUMMARY_TEST_ID_PATTERN.md
 */

export const setImageCellTestId = (index: number): string => generateTestId("ProductsTable", "image-cell", index);

export const setNameCellTestId = (index: number): string => generateTestId("ProductsTable", "name-cell", index);

export const setBagSizeCellTestId = (index: number): string => generateTestId("ProductsTable", "bagsize-cell", index);

export const setSourceCellTestId = (index: number): string => generateTestId("ProductsTable", "source-cell", index);

export const setStatusCellTestId = (index: number): string => generateTestId("ProductsTable", "status-cell", index);

export const setShipToCellTestId = (index: number): string => generateTestId("ProductsTable", "shipto-cell", index);

export const setShipToTriggerTestId = (index: number): string => generateTestId("ProductsTable", "shipto-trigger", index);

export const setShipToPopoverContentTestId = (index: number): string => generateTestId("ProductsTable", "shipto-popover", index);

export const setShipToCountryRowTestId = (index: number, countryCode: string): string =>
  generateTestId("ProductsTable", `shipto-country-${countryCode}`, index);

export const setShipToFlagTestId = (index: number, countryCode: string): string =>
  generateTestId("ProductsTable", `shipto-flag-${countryCode}`, index);

export const setVariantsCellTestId = (index: number): string => generateTestId("ProductsTable", "variants-cell", index);

export const setSubscribersCellTestId = (index: number): string => generateTestId("ProductsTable", "subscribers-cell", index);

export const setWholesalePriceCellTestId = (index: number): string => generateTestId("ProductsTable", "wholesaleprice-cell", index);

export const setWholesalePriceEditButtonTestId = (index: number): string =>
  generateTestId("ProductsTable", "wholesaleprice-edit-button", index);

export const setMsrpRrpCellTestId = (index: number): string => generateTestId("ProductsTable", "msrprrp-cell", index);

export const setMsrpRrpEditButtonTestId = (index: number): string => generateTestId("ProductsTable", "msrprrp-edit-button", index);

export const setWeeklyLimitCellTestId = (index: number): string => generateTestId("ProductsTable", "weeklylimit-cell", index);

export const setTermCellTestId = (index: number): string => generateTestId("ProductsTable", "term-cell", index);

export const setExpectedOutOfStockCellTestId = (index: number): string => generateTestId("ProductsTable", "expectedoutofstock-cell", index);

export const setRequestsCellTestId = (index: number): string => generateTestId("ProductsTable", "requests-cell", index);

export const setPendingBadgeTestId = (index: number): string => generateTestId("ProductsTable", "pending-badge", index);

export const setPendingRequestsModalTestId = (): string => generateTestId("RequestsCell", "pending-modal");

export const setPendingRequestsModalRowTestId = (index: number): string => generateTestId("RequestsCell", `pending-modal-row-${index}`);

export const setActionsCellTestId = (index: number): string => generateTestId("ProductsTable", "actions-cell", index);

export const setActionsEditButtonTestId = (index: number): string => generateTestId("ProductsTable", "actions-edit-button", index);

export const setActionsDisconnectButtonTestId = (index: number): string =>
  generateTestId("ProductsTable", "actions-disconnect-button", index);

export const setActionsMapProductButtonTestId = (index: number): string =>
  generateTestId("ProductsTable", "actions-map-product-button", index);

export const setActionsCollapsedButtonTestId = (index: number): string =>
  generateTestId("ProductsTable", "actions-collapsed-button", index);

/**
 * Expand/collapse button test ID for products with multiple variants
 */
export const setExpandButtonTestId = (index: number): string => generateTestId("ProductsTable", "expand-button", index);

/**
 * Data transformation and table helper utilities
 */

/**
 * Extracts the best available image URL for a product.
 *
 * Priority: PIM image > draft/uploaded image > empty string.
 * The empty string allows callers (e.g. ProductThumbnail) to render
 * their own placeholder when no image is available.
 *
 * @param images       - ProductImage array from FormProduct
 * @param imageUploads - UploadImage array from draft or form state
 * @returns Resolved image URL or empty string
 *
 * @example
 * const imageUrl = getProductImage(product.images, product.imageUploads);
 */
export const getProductImage = (images?: ProductImage[], imageUploads?: FormProduct["imageUploads"]): string => {
  const resolved = resolveProductImage(images, imageUploads);
  // Return empty string instead of placeholder for table context
  // (ProductThumbnail handles its own placeholder)
  return resolved === PLACEHOLDER_IMAGE ? "" : resolved;
};

// Re-export from the neutral lib module so existing callers keep working
// while route-level utils can import the helper directly from the lib.
export { resolveWebStatus };

/**
 * Parses the Salesforce `ship_to` field — a ";"-separated list of ISO country
 * codes such as `"NL;DE"` — into the per-country rows the cross-border
 * "Ship to" cell and popover render. Whitespace is trimmed, codes are
 * upper-cased, and unsupported or duplicate codes are dropped (only the
 * cross-border destinations NL/DE are recognised today).
 *
 * Per-country pricing is not yet returned by the product fetch, so each
 * country mirrors the SKU's own wholesale/retail price and subscriber count
 * until the backend exposes a per-country breakdown.
 *
 * @example
 * buildShipToCountryData("NL;DE", variant); // [{ countryCode: "NL", ... }, { countryCode: "DE", ... }]
 */
export const buildShipToCountryData = (shipTo: string | null | undefined, variant: FormVariant): ShipToCountryData[] => {
  // Prefer the per-channel breakdown from the product fetch — it carries each
  // ship-to country's real price / wholesale / subscriber numbers. Fall back to
  // the Salesforce `ship_to` string (mirroring the SKU's own price) when the
  // per-channel data isn't present.
  const fromShipToInfo = buildShipToCountryDataFromInfo(variant.shipToInfo);
  if (fromShipToInfo.length > 0) return fromShipToInfo;
  if (!shipTo) return [];
  const wholesalePrice = parsePrice(variant.wholesalePrice);
  const retailPrice = parsePrice(variant.price);
  const subscribers = variant.activeSubscriberCount ?? 0;
  const seen = new Set<CrossBorderCountries>();
  const countries: ShipToCountryData[] = [];
  for (const segment of shipTo.split(";")) {
    const parsed = CrossBorderCountriesSchema.safeParse(segment.trim().toUpperCase());
    if (!parsed.success || seen.has(parsed.data)) continue;
    seen.add(parsed.data);
    countries.push({ countryCode: parsed.data, wholesalePrice, retailPrice, subscribers });
  }
  return countries;
};

/**
 * Maps a variant's per-channel `shipToInfo` (from the PIM product fetch) into
 * the cross-border "Ship to" rows, resolving each channel to its country
 * (e.g. "BNLMP" → NL). Unrecognised / duplicate countries are dropped (only the
 * cross-border destinations NL/DE are rendered).
 */
const buildShipToCountryDataFromInfo = (shipToInfo: ShipToInfo[] | undefined): ShipToCountryData[] => {
  if (!shipToInfo?.length) return [];
  const seen = new Set<CrossBorderCountries>();
  const countries: ShipToCountryData[] = [];
  for (const info of shipToInfo) {
    const parsed = CrossBorderCountriesSchema.safeParse(parseChannelCountryCode(info.channel));
    if (!parsed.success || seen.has(parsed.data)) continue;
    seen.add(parsed.data);
    countries.push({
      countryCode: parsed.data,
      wholesalePrice: parsePrice(info.wholesalePrice),
      retailPrice: parsePrice(info.price),
      subscribers: info.activeSubscriberCount ?? 0,
    });
  }
  return countries;
};

// Re-exported from the neutral lib module so cells and the products table keep
// importing it from here, while the cross-border pricing UI imports it directly.
export { vendorRegionToCrossBorderCountry, homeRegionForVendor } from "@bcc/lib/products/cross-border-tax";

/**
 * Creates a VariantWithProductData object by merging a FormVariant with FormProduct data.
 * This function denormalizes the hierarchical product/variant structure into a flat row.
 *
 * @param variant - The FormVariant to transform
 * @param product - The FormProduct containing the variant
 * @param serviceId - The vendor system service id (drives the source badge)
 * @returns VariantWithProductData with all variant fields plus denormalized product fields
 *
 * @example
 * const rowData = createVariantWithProductData(variant, product);
 * // Returns flat row with both variant and product data
 */
export const createVariantWithProductData = (variant: FormVariant, product: FormProduct, serviceId?: string): VariantWithProductData => {
  // Determine source: if product has localId or any variant has isImportedLocalVariant=true, it's 'shopify'
  const hasVariantLocalId = product.variants?.some((v) => v.isImportedLocalVariant) ?? false;
  let source: "manual" | "shopify" | "woocommerce" = "manual";
  if (product.localId || hasVariantLocalId) {
    source = "shopify";
  } else if (serviceId === "woocommerce") {
    source = "woocommerce";
  }

  return {
    ...variant,
    // The row carries the flat resolved status / weekly inventory. Per-country
    // ship-to resolution against the multi-channel `shipToInfo` happens in the
    // Status and Weekly Limit cells for the logged-in country, using these
    // values as the fallback for non-cross-border rows.
    webStatus: resolveWebStatus(variant.webStatus, variant.status),
    weeklyInventory: variant.weeklyInventory,
    productId: product.id,
    productTitle: product.title ?? "No Product",
    productSku: product.localId ?? product.id,
    category: product.productType ?? "Coffee",
    imageUrl: getProductImage(product.images, product.imageUploads),
    description: product.description ?? "",
    source,
    roasterNotes: product.roasterNotes,
    mainFlavorCategory: product.mainFlavorCategory,
    secondaryFlavorCategory: product.secondaryFlavorCategory,
    isDecaf: product.isDecaf,
    roastLevel: product.roastLevel,
    process: product.process,
    varietal: product.varietal,
    tastingNotes: product.tastingNotes,
    brewMethod: product.brewMethod,
    certifications: product.certifications,
    region: product.region,
    country: product.country,
    elevationStart: product.elevationStart,
    elevationEnd: product.elevationEnd,
    // Cross-border per-country breakdown parsed from the Salesforce `ship_to`
    // field (e.g. "NL;DE"). Empty for non-cross-border SKUs.
    shipTo: buildShipToCountryData(product.ship_to, variant),
  } satisfies VariantWithProductData;
};

/**
 * Transforms an array of FormProducts into a flattened array of VariantWithProductData.
 * Each product's variants are mapped to individual rows with denormalized product data.
 *
 * @param products - Array of FormProducts from ProductsProvider (can be undefined)
 * @param serviceId - The vendor system service id (drives the source badge)
 * @returns Flattened array of VariantWithProductData for TanStack Table
 *
 * @example
 * const tableData = getLocalVariantsWithProductData(formProducts);
 * // Returns flat array of all variants with their product data
 */
export const getLocalVariantsWithProductData = (products: FormProduct[] | undefined, serviceId?: string): VariantWithProductData[] => {
  if (!products || products.length === 0) {
    return [];
  }

  return products.flatMap((product) => {
    const variants = product?.variants ?? [];

    // Group variants by bagSize, combining grind arrays into one row per bag size
    const bagSizeGroups = new Map<string, FormVariant[]>();
    variants.forEach((variant) => {
      const key = variant.bagSize ?? "";
      if (!bagSizeGroups.has(key)) {
        bagSizeGroups.set(key, []);
      }
      bagSizeGroups.get(key)?.push(variant);
    });

    return Array.from(bagSizeGroups.values()).flatMap((groupVariants) => {
      const baseVariant = groupVariants[0];
      if (!baseVariant) return [];
      const combinedGrinds = Array.from(new Set(groupVariants.flatMap((variant) => variant.grind ?? [])));
      const groupTargetVariantIds = groupVariants
        .map((variant) => variant.id)
        .filter((id): id is string => typeof id === "string" && id.length > 0);
      const row = createVariantWithProductData(baseVariant, product, serviceId);
      const rowWithGroupIds = { ...row, groupTargetVariantIds };
      return [combinedGrinds.length > 0 ? { ...rowWithGroupIds, grind: combinedGrinds } : rowWithGroupIds];
    });
  });
};

export const PENDING_APPROVAL_STATUS = "Submitted for Approval" as const;
export const APPROVED_STATUS = "Approved" as const;
export const REJECTED_STATUS = "Rejected" as const;

interface PendingApprovalFlags {
  hasPendingApproval: boolean;
  hasPendingStatusApproval: boolean;
  hasPendingPriceApproval: boolean;
  hasPendingWeeklyLimitApproval: boolean;
  // Distinct Performed_Action__c values present on the row (after excluding
  // Rejected). Drives the "N Pending" badge in the Requests cell. Counted as
  // distinct action types — two records for "Inventory Update" still count
  // as 1 locked editor, not 2.
  pendingActionCount: number;
}

const EMPTY_PENDING_APPROVAL_FLAGS: PendingApprovalFlags = {
  hasPendingApproval: false,
  hasPendingStatusApproval: false,
  hasPendingPriceApproval: false,
  hasPendingWeeklyLimitApproval: false,
  pendingActionCount: 0,
};

const createEmptyPendingApprovalFlags = (): PendingApprovalFlags => ({
  ...EMPTY_PENDING_APPROVAL_FLAGS,
});

/**
 * Converts row approval records into field-specific edit lockout flags.
 *
 * Any non-Rejected record locks the matching editor — both "Submitted for
 * Approval" and "Approved" roll into the same lockout, since an Approved
 * change isn't yet deployed and a second edit on top of it would diverge
 * from what Salesforce has on file. Rejected records are skipped because
 * the row is back under roaster control once a request is rejected.
 *
 * Performed_Action__c maps to the popover that fired the request:
 * Status Change, Price Update, and Inventory Update.
 */
export const getPendingApprovalFlags = (records?: readonly ApprovalStatusResponseRecord[]): PendingApprovalFlags => {
  if (!records || records.length === 0) {
    return createEmptyPendingApprovalFlags();
  }
  const flags: PendingApprovalFlags = createEmptyPendingApprovalFlags();
  for (const record of records) {
    if (record.Approval_status__c === REJECTED_STATUS) continue;
    flags.hasPendingApproval = true;
    switch (record.Performed_Action__c) {
      case "Status Change":
        flags.hasPendingStatusApproval = true;
        break;
      case "Price Update":
        flags.hasPendingPriceApproval = true;
        break;
      case "Inventory Update":
        flags.hasPendingWeeklyLimitApproval = true;
        break;
      default:
        flags.hasPendingStatusApproval = true;
        flags.hasPendingPriceApproval = true;
        flags.hasPendingWeeklyLimitApproval = true;
        break;
    }
  }
  flags.pendingActionCount =
    (flags.hasPendingStatusApproval ? 1 : 0) + (flags.hasPendingPriceApproval ? 1 : 0) + (flags.hasPendingWeeklyLimitApproval ? 1 : 0);
  return flags;
};

/**
 * Builds a Map from PIM SKU code to approval request records.
 * Used to stamp `approvalRequestsForRow` on enriched table rows.
 */
export const buildRequestsBySkuMap = (records: ApprovalStatusResponseRecord[]): Map<string, ApprovalStatusResponseRecord[]> => {
  const map = new Map<string, ApprovalStatusResponseRecord[]>();
  for (const r of records) {
    const list = map.get(r.Item_numbers__c) ?? [];
    list.push(r);
    map.set(r.Item_numbers__c, list);
  }
  return map;
};

// =============================================================================
// Table helper utilities (extracted from component for testability)
// =============================================================================

export { parsePrice, formatPrice } from "@bcc/lib/utils";

/**
 * Maps a resolved webStatus string to a lowercase status for popover/modal components.
 * After resolveWebStatus, the possible values are:
 * "Visible for Sale", "Not Visible", "Out of Stock", "Rejected", "Pending"
 */
export const mapWebStatusToStatus = (webStatus?: string): "published" | "out_of_stock" | "draft" | "rejected" => {
  switch (webStatus) {
    case "Visible for Sale":
      return "published";
    // "Visible but not for Sale" means the SKU is on the storefront but
    // cannot be bought, and "Temporarily Out of Stock" is not currently
    // purchasable, so the popover treats both the same as Out of Stock.
    case "Out of Stock":
    case "Temporarily Out of Stock":
    case "Visible but not for Sale":
      return "out_of_stock";
    case "Rejected":
      return "rejected";
    case "Not Visible":
    case "Pending":
    default:
      return "draft";
  }
};

/**
 * Maps a term string to a termType for the Product interface.
 */
export const mapTermToTermType = (term?: string): "long_term" | "short_term" => {
  return term === "Short" ? "short_term" : "long_term";
};

/**
 * Finds a product from variant data by productId and reconstructs the Product shape.
 * Normalizes productId to string for comparison to handle mixed types.
 */
export function findProductByIdFromVariants(productId: string | number | null, data: VariantWithProductData[]): Product | null {
  if (!productId) return null;
  const normalizedId = String(productId);
  const variant = data.find((v) => String(v.productId) === normalizedId);
  if (!variant) return null;

  return {
    id: productId,
    name: variant.productTitle ?? "",
    sku: variant.productSku ?? "",
    category: variant.category ?? "",
    imageUrl: variant.imageUrl ?? "",
    description: variant.description ?? "",
    source: variant.source ?? "manual",
    vendorKey: "",
    roasterNotes: variant.roasterNotes,
    mainFlavorCategory: variant.mainFlavorCategory,
    secondaryFlavorCategory: variant.secondaryFlavorCategory,
    isDecaf: variant.isDecaf,
    roastLevel: variant.roastLevel,
    process: variant.process,
    varietal: variant.varietal,
    tastingNotes: variant.tastingNotes,
    brewMethod: variant.brewMethod,
    certifications: variant.certifications,
    region: variant.region,
    country: variant.country,
    elevationStart: variant.elevationStart,
    elevationEnd: variant.elevationEnd,
    variants: data
      .filter((v) => String(v.productId) === normalizedId)
      .map((variant) => ({
        id: variant.id ?? "",
        size: variant.bagSize ?? "",
        sku: "",
        status: mapWebStatusToStatus(variant.webStatus),
        subscribers: variant.activeSubscriberCount ?? 0,
        purchasePrice: parsePrice(variant.wholesalePrice),
        salePrice: parsePrice(variant.price),
        commission: 0,
        payout: 0,
        margin: 0,
        weeklyLimit: variant.weeklyInventory ?? variant.maxBagsShippedPerWeek ?? 0,
        termType: mapTermToTermType(variant.term),
        termEndDate: variant.termEndDate,
        expectedOutOfStockDate: variant.expectedOutOfStockDate,
        grindMappings: variant.grind ?? [],
        stock: variant.inventoryQuantity ?? 0,
      })),
  };
}

// =============================================================================
// Search / global-filter helpers
// =============================================================================

/**
 * Maps a raw webStatus to the translation-key suffix used in
 * `views.productsTable.status.<key>`. A direct switch (not routed through
 * `mapWebStatusToStatus`) because that helper collapses "Not Visible" and
 * "Pending" into the same "draft" bucket for popover actions, losing the
 * distinction we need for search labels. Returns `null` for values with no
 * label so callers can decide the fallback.
 */
const getStatusTranslationKey = (webStatus?: string): string | null => {
  switch (webStatus) {
    case "Visible for Sale":
      return "published";
    case "Pending":
      return "pending";
    case "Not Visible":
      return "notVisible";
    // "Visible but not for Sale" and "Temporarily Out of Stock" render under the
    // same "Out of Stock" label.
    case "Out of Stock":
    case "Temporarily Out of Stock":
    case "Visible but not for Sale":
      return "outOfStock";
    case "Rejected":
      return "rejected";
    default:
      return null;
  }
};

/**
 * i18n paths that carry grind-type display labels. Grind values from PIM are
 * always in English, so we discover the matching i18n key by scanning the
 * EN locale, then pull each key's label in the *active* locale to add to the
 * index. Non-EN search text (e.g. DE "Ganze Bohne") then matches correctly.
 */
const GRIND_TRANSLATION_PATHS = ["ssRoaster.skuOnboarding.grindOptions", "ssRoaster.skuOnboarding.brewMethodOptions"] as const;

const normalizeGrindValue = (value: string): string => value.trim().toLowerCase();

/**
 * Normalized-EN-label → localized labels. Built once per `t` at the component
 * level via `useMemo(() => buildGrindLookup(t), [t])`, then passed into
 * `buildRowSearchIndex` so per-row enrichment is O(grinds) instead of
 * O(rows × grinds × optionCount).
 */
export type GrindLookup = ReadonlyMap<string, readonly string[]>;

/**
 * Walks `GRIND_TRANSLATION_PATHS` in EN, resolves each key in the active locale,
 * and returns a Map keyed on the normalized English label. Cheap to call once
 * per `t` instance; expensive to call per row — use `useMemo`.
 */
const addLocalizedLabel = (lookup: Map<string, string[]>, normalizedKey: string, localizedLabel: string): void => {
  const existing = lookup.get(normalizedKey);
  if (!existing) {
    lookup.set(normalizedKey, [localizedLabel]);
  } else if (!existing.includes(localizedLabel)) {
    existing.push(localizedLabel);
  }
};

const addGrindPathToLookup = (lookup: Map<string, string[]>, t: TFunction, path: string): void => {
  const englishOptions = t(path, {
    lng: "en",
    returnObjects: true,
    defaultValue: {},
  });
  if (typeof englishOptions !== "object" || englishOptions === null) return;
  for (const [key, englishLabel] of Object.entries(englishOptions as Record<string, unknown>)) {
    if (typeof englishLabel !== "string") continue;
    // Cast through unknown: the dynamic `${path}.${key}` argument makes
    // i18next's strict TFunction return type narrow to `never`.
    const localizedLabel = t(`${path}.${key}`, {
      defaultValue: englishLabel,
    }) as unknown;
    if (typeof localizedLabel !== "string" || localizedLabel.length === 0) {
      continue;
    }
    addLocalizedLabel(lookup, normalizeGrindValue(englishLabel), localizedLabel);
  }
};

export const buildGrindLookup = (t: TFunction): GrindLookup => {
  const lookup = new Map<string, string[]>();
  for (const path of GRIND_TRANSLATION_PATHS) {
    addGrindPathToLookup(lookup, t, path);
  }
  return lookup;
};

const getLocalizedGrind = (raw: string, lookup: GrindLookup): readonly string[] => lookup.get(normalizeGrindValue(raw)) ?? [];

/**
 * Builds a lowercased, space-joined string of every searchable field on a
 * product row — raw values plus their localized equivalents for status,
 * grind, and source. Stamp this once per row change and the custom
 * globalFilterFn can substring-match without re-translating per keystroke.
 *
 * Pass a pre-built `grindLookup` (see `buildGrindLookup`) when calling per row
 * to avoid re-scanning the EN translation bundle on every invocation. If
 * omitted (e.g. in direct tests), the lookup is built lazily per call.
 *
 * Pass the logged-in vendor's `countryCode` so the indexed status matches the
 * per-country value the Status cell renders (and the status filter chips
 * match); omitting it indexes the row-level `webStatus`.
 */
export const buildRowSearchIndex = (
  row: VariantWithProductData,
  t: TFunction,
  grindLookup?: GrindLookup,
  countryCode?: VendorCountryCode
): string => {
  // Index the status the badge actually shows: cross-border SKUs carry a
  // per-country web status in `shipToInfo`, so the current country's status
  // wins over the row-level one. Without this a GB row whose `BUKMP` entry is
  // "Visible but not for Sale" would display "Out of Stock" yet only be
  // findable by searching the stale row-level "Published".
  const displayedWebStatus = resolveShipToWebStatus(row.shipToInfo, countryCode, row.webStatus);
  // Fall back to the "pending" key when the webStatus isn't explicitly mapped,
  // matching StatusCellMarkup's renderer so any unmapped WebStatus value is
  // still findable by searching the label the badge actually shows.
  const statusKey = displayedWebStatus ? (getStatusTranslationKey(displayedWebStatus) ?? "pending") : undefined;
  const statusLabel = statusKey ? String(t(`views.productsTable.status.${statusKey}`)) : "";
  const grinds = row.grind ?? [];
  const lookup = grindLookup ?? buildGrindLookup(t);
  const translatedGrinds = grinds.flatMap((grind) => getLocalizedGrind(grind, lookup));
  const sourceLabel = row.source ? String(t(`views.productsTable.source.${row.source}`)) : "";
  const parts: Array<string | undefined> = [
    row.productTitle,
    row.productSku,
    row.bagSize,
    row.title,
    displayedWebStatus,
    statusLabel,
    ...grinds,
    ...translatedGrinds,
    row.source,
    sourceLabel,
  ];
  return parts
    .filter((value): value is string => typeof value === "string" && value.length > 0)
    .join(" ")
    .toLowerCase();
};

interface IndexedRow extends VariantWithProductData {
  __searchIndex?: string;
}

/**
 * Custom TanStack Table globalFilterFn for the products table. Matches a
 * pre-built `__searchIndex` on the row (see `buildRowSearchIndex`), falling
 * back to computing on the fly if the stamp isn't present. Pass the same
 * memoized `grindLookup` and vendor `countryCode` used to build
 * `__searchIndex` so the fallback path stays cheap and resolves the same
 * per-country status.
 */
export const createProductsGlobalFilterFn =
  (t: TFunction, grindLookup?: GrindLookup, countryCode?: VendorCountryCode): FilterFn<VariantWithProductData> =>
  // `_addMeta` is unused but declared: `FilterFn` takes four parameters, and
  // every call site must pass all four. Omitting it here leaves callers passing
  // an argument the signature doesn't declare, which static analysis reads as a
  // superfluous trailing argument.
  (row, _columnId, filterValue, _addMeta) => {
    if (typeof filterValue !== "string") return true;
    const normalizedFilterValue = filterValue.trim().toLowerCase();
    if (normalizedFilterValue === "") return true;
    const indexed = (row.original as IndexedRow).__searchIndex ?? buildRowSearchIndex(row.original, t, grindLookup, countryCode);
    return indexed.includes(normalizedFilterValue);
  };

/**
 * Result type for groupVariantsByVisibility.
 */
export interface VariantVisibilityResult {
  visibleData: VariantWithProductData[];
  expandableProducts: Set<string>;
  firstVariantIds: Set<string>;
}

/**
 * Groups variants by product and determines visibility based on expansion state.
 * Single-variant products always show. Multi-variant products show only the first
 * variant unless expanded.
 */
export function groupVariantsByVisibility(
  filteredData: VariantWithProductData[],
  expandedProductIds: Set<string>
): VariantVisibilityResult {
  const productVariants = new Map<string, VariantWithProductData[]>();

  // Group variants by productId
  filteredData.forEach((variant) => {
    const productId = String(variant.productId);
    if (!productVariants.has(productId)) {
      productVariants.set(productId, []);
    }
    productVariants.get(productId)?.push(variant);
  });

  // Determine which products can expand (have multiple variants)
  const expandable = new Set<string>();
  const firstVariants = new Set<string>();

  productVariants.forEach((variants, productId) => {
    if (variants.length > 1) {
      expandable.add(productId);
    }
    if (variants[0]) {
      firstVariants.add(variants[0].id);
    }
  });

  // Filter visible rows: show first variant always, additional variants only if expanded
  const visible: VariantWithProductData[] = [];
  productVariants.forEach((variants, productId) => {
    const isExpanded = expandedProductIds.has(productId);

    if (variants.length === 1) {
      if (variants[0]) visible.push(variants[0]);
    } else if (isExpanded) {
      visible.push(...variants);
    } else if (variants[0]) {
      visible.push(variants[0]);
    }
  });

  return {
    visibleData: visible,
    expandableProducts: expandable,
    firstVariantIds: firstVariants,
  };
}

export const PENDING_REQUEST_COLUMNS = ["fieldsChanged", "effectiveDate", "roasterComments", "bmComments"] as const;

export type PendingRequestColumn = (typeof PENDING_REQUEST_COLUMNS)[number];

export const setPendingRequestsModalCellTestId = (index: number, column: PendingRequestColumn): string =>
  generateTestId("RequestsCell", `pending-modal-row-${index}-${column}`);

export const setPendingRequestsModalReadMoreTestId = (index: number, column: PendingRequestColumn): string =>
  generateTestId("RequestsCell", `pending-modal-row-${index}-${column}-read-more`);
